/**
 * Action: create_platform_request
 *
 * Route Forge product feedback from Solomon capture into the
 * public.platform_requests dev/support queue. This mirrors Relay's
 * quick-capture routing, but uses Forge's public schema.
 */

import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { logAudit } from "@/lib/audit";
import {
  FORGE_CAPTURE_CATEGORIES,
  coerceForgeCaptureCategory,
} from "@/lib/forge-capture-classification";
import { registerAction } from "../registry";
import type { ActionDefinition, ActionResult } from "../types";

const platformRequestTypes = ["feature", "bug", "feedback"] as const;
const platformPriorities = ["low", "normal", "high", "urgent"] as const;

const createPlatformRequestParams = z.object({
  type: z.enum(platformRequestTypes),
  subject: z.string().trim().min(1).max(500),
  description: z.string().trim().max(5000).optional().nullable(),
  priority: z.enum(platformPriorities).default("normal"),
  capture_category: z
    .preprocess(
      coerceForgeCaptureCategory,
      z.enum(FORGE_CAPTURE_CATEGORIES),
    )
    .optional(),
});

type CreatePlatformRequestParams = z.infer<typeof createPlatformRequestParams>;
type PlatformRequestInsert =
  Database["public"]["Tables"]["platform_requests"]["Insert"];

const TYPE_LABELS: Record<CreatePlatformRequestParams["type"], string> = {
  feature: "Forge improvement",
  bug: "Forge bug",
  feedback: "Forge feedback",
};

const createPlatformRequestDefinition: ActionDefinition<CreatePlatformRequestParams> = {
  id: "create_platform_request",
  label: "Create Forge platform request",
  description:
    "Send a Forge app bug, improvement, or feedback item to the platform request queue.",
  requiredTier: "member",
  paramsSchema: createPlatformRequestParams,
  defaultRequiresConfirm: true,
  async buildPreview(params) {
    return {
      summary: `Send ${TYPE_LABELS[params.type].toLowerCase()} "${params.subject}" to the Forge queue`,
    };
  },
  async execute(params, ctx): Promise<ActionResult> {
    const insert: PlatformRequestInsert = {
      organization_id: ctx.organizationId,
      submitted_by: ctx.userId,
      subject: params.subject,
      description: params.description ?? null,
      type: params.type,
      priority: params.priority,
      status: "open",
      source: "solomon_capture",
    };

    const { data, error } = await supabase
      .from("platform_requests")
      .insert(insert)
      .select("id")
      .single();

    if (error || !data) {
      return {
        ok: false,
        summary: "Failed to create Forge platform request",
        error: error?.message ?? "Unknown insert failure",
      };
    }

    const audit = await logAudit({
      action: "platform_request.create",
      tableName: "platform_requests",
      recordId: data.id,
      organizationId: ctx.organizationId,
      userId: ctx.userId,
      newData: {
        type: params.type,
        subject: params.subject,
        priority: params.priority,
        capture_category: params.capture_category ?? null,
        source: ctx.source,
        reason: ctx.reason,
      },
    });

    return {
      ok: true,
      summary: `Sent ${TYPE_LABELS[params.type].toLowerCase()} "${params.subject}" to the Forge queue`,
      auditLogId: audit?.id ?? null,
      inverse: null,
    };
  },
};

registerAction(createPlatformRequestDefinition);

export { createPlatformRequestDefinition };
